import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DOC_TYPES, data, portal, type Item, type Page } from "@aawaz/api-client";
import {
  Badge, Button, Card, Drawer, EmptyState, ErrorBanner, MonthPicker, PageHeader, Select, SkeletonRows, Table, Tabs, fmtDate, humanize, sourceLabel,
} from "@aawaz/ui";
import { ItemPanel, useAct, useClients, type Action, type DocType } from "../components/ItemPanel";
import { useSession } from "../session";
import { displayName, FileIcon } from "./Documents";

const DAY = 86_400_000;

/** One place for everything that needs a person: documents to review, and the ones held back
 *  (quarantined) with how long they are kept. Opens each document in a side panel. */
export function InboxPage() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "held" ? "held" : "review";
  const [open, setOpen] = useState<string | null>(sp.get("open"));
  const review = useInfiniteQuery({
    queryKey: ["items", "review"], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => data<Page<Item>>(portal.GET("/portal/v1/review-queue",
      { params: { query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    getNextPageParam: (p) => p.next_cursor ?? null,
  });
  const held = useInfiniteQuery({
    queryKey: ["items", "quarantine"], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => data<Page<Item>>(portal.GET("/portal/v1/items", { params: { query: {
      status: "QUARANTINED", limit: 50, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    getNextPageParam: (p) => p.next_cursor ?? null,
  });
  const nReview = review.data?.pages[0]?.data.length ?? 0;
  const nHeld = held.data?.pages[0]?.data.length ?? 0;
  const close = useCallback(() => {
    setOpen(null);
    if (sp.get("open")) { const n = new URLSearchParams(sp); n.delete("open"); setSp(n, { replace: true }); }
  }, [sp, setSp]);
  return (
    <>
      <PageHeader title="Inbox" description="Documents AawazAI could not file on its own, and the ones it held back." />
      <Tabs value={tab} onChange={(v) => { const n = new URLSearchParams(sp); v === "held" ? n.set("tab", "held") : n.delete("tab"); setSp(n, { replace: true }); }}
            tabs={[{ value: "review", label: "To review", count: nReview }, { value: "held", label: "Held back", count: nHeld }]} />
      {tab === "review" ? <ReviewList q={review} onOpen={setOpen} drawerOpen={!!open} /> : <HeldList q={held} onOpen={setOpen} />}
      <Drawer open={!!open} title="Document" onClose={close}>
        {open ? <ItemPanel id={open} onDone={close} /> : null}
      </Drawer>
    </>
  );
}

type Q = ReturnType<typeof useInfiniteQuery<Page<Item>>>;

function ReviewList({ q, onOpen, drawerOpen }: { q: Q; onOpen: (id: string) => void; drawerOpen: boolean }) {
  const s = useSession();
  const qc = useQueryClient();
  const clients = useClients();
  const rows = useMemo(() => (q.data?.pages as Page<Item>[] | undefined)?.flatMap((p) => p.data) ?? [], [q.data]);
  const [focus, setFocus] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const bulk = useCallback(async (a: Action, ids?: string[]) => {
    const target = ids ?? [...selected];
    if (!target.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await data<{ results: { id: string; ok: boolean; error?: string }[] }>(
        portal.POST("/portal/v1/items:bulk", { body: { ...a, item_ids: target } }));
      const failed = res.results.filter((r) => !r.ok);
      if (failed.length) setError(new Error(`${res.results.length - failed.length} updated; ${failed.length} could not be (${failed[0]?.error}). They stay selected.`));
      setSelected(new Set(failed.map((r) => r.id)));
      await qc.invalidateQueries({ queryKey: ["items"] });
      await qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [selected, qc]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (drawerOpen || (e.target as HTMLElement).closest("input, select, textarea, [role=dialog]")) return;
      const row = rows[focus];
      if (e.key === "j") setFocus((f) => Math.min(f + 1, rows.length - 1));
      else if (e.key === "k") setFocus((f) => Math.max(f - 1, 0));
      else if (e.key === "x" && row) setSelected((s0) => { const n = new Set(s0); n.has(row.id) ? n.delete(row.id) : n.add(row.id); return n; });
      else if (e.key === "o" && row) onOpen(row.id);
      else if (e.key === "a" && s.can("review")) void bulk({ action: "accept" }, selected.size ? undefined : row ? [row.id] : []);
      else if (e.key === "q" && s.can("review")) void bulk({ action: "quarantine", reason: "inbox" }, selected.size ? undefined : row ? [row.id] : []);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, focus, drawerOpen, selected, bulk, s, onOpen]);
  const names = new Map((clients.data?.data ?? []).map((c) => [c.id, c.display_name]));
  return (
    <>
      <ErrorBanner error={error ?? q.error} />
      {s.can("review") && selected.size > 0 ? (
        <Card className="ui-card-muted">
          <div className="ui-row">
            <strong style={{ marginRight: 8 }}>{selected.size} selected</strong>
            <Button variant="primary" size="sm" icon="check" busy={busy} onClick={() => void bulk({ action: "accept" })}>Accept</Button>
            <Button variant="danger" size="sm" busy={busy} onClick={() => void bulk({ action: "quarantine", reason: "inbox" })}>Hold back</Button>
            <Select aria-label="Set client" value="" style={{ width: 200 }} onChange={(e) => e.target.value && void bulk({ action: "set_client", client_org_id: e.target.value })}
                    options={[{ value: "", label: "Set client…" }, ...(clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name }))]} />
            <Select aria-label="Set type" value="" style={{ width: 180 }} onChange={(e) => e.target.value && void bulk({ action: "reclassify", doc_type: e.target.value as DocType })}
                    options={[{ value: "", label: "Set type…" }, ...DOC_TYPES.map((d) => ({ value: d, label: humanize(d) }))]} />
            <MonthPicker aria-label="Set month" style={{ width: 160 }} value="" anyLabel="Set month…"
                         onChange={(v) => v && void bulk({ action: "reclassify", period: v })} />
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        </Card>
      ) : null}
      <Card flush>
        {q.isLoading ? <div style={{ padding: 22 }}><SkeletonRows /></div> : rows.length === 0 ? (
          <EmptyState icon="checkCircle" title="All clear">Nothing needs review. New documents that need a decision appear here.</EmptyState>
        ) : (
          <Table rows={rows} rowKey={(r) => r.id} focusIndex={focus} selected={selected}
                 onToggle={(id) => setSelected((s0) => { const n = new Set(s0); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                 onRowClick={(r) => onOpen(r.id)} columns={[
            { key: "f", header: "Document", render: (r) => (
              <div className="ui-cell-main"><FileIcon item={r} /><div style={{ minWidth: 0 }}>
                <span className="ui-cell-title">{displayName(r)}</span>
                <span className="ui-cell-sub">{sourceLabel(r.source)}{r.sender?.name ? ` · ${r.sender.name}` : ""}</span></div></div>) },
            { key: "r", header: "Why", render: (r) => <Badge value={r.reason_code ?? r.status} tone="amber" /> },
            { key: "c", header: "Client", render: (r) => (r.client_org_id ? names.get(r.client_org_id) : <span className="ui-hint">Not known</span>) ?? "…" },
            { key: "t", header: "Looks like", render: (r) => (r.doc_type ? humanize(r.doc_type) : <span className="ui-hint">—</span>) },
            { key: "d", header: "Received", render: (r) => <span className="ui-hint">{fmtDate(r.received_at)}</span> },
          ]} />
        )}
        {rows.length ? (
          <div className="ui-table-foot">
            <span>{rows.length} shown · <kbd>j</kbd> <kbd>k</kbd> move · <kbd>x</kbd> select · <kbd>a</kbd> accept · <kbd>q</kbd> hold back · <kbd>o</kbd> open</span>
            {q.hasNextPage ? <Button size="sm" onClick={() => void q.fetchNextPage()} busy={q.isFetchingNextPage}>Load more</Button> : null}
          </div>
        ) : null}
      </Card>
    </>
  );
}

function HeldList({ q, onOpen }: { q: Q; onOpen: (id: string) => void }) {
  const s = useSession();
  const act = useAct();
  const rows = (q.data?.pages as Page<Item>[] | undefined)?.flatMap((p) => p.data) ?? [];
  const daysLeft = (r: Item) => {
    const keep = r.reason_code === "MALWARE" ? 7 : 30;
    return Math.max(0, Math.ceil((new Date(r.updated_at).getTime() + keep * DAY - Date.now()) / DAY));
  };
  return (
    <>
      <ErrorBanner error={q.error ?? act.error} />
      <Card flush>
        {q.isLoading ? <div style={{ padding: 22 }}><SkeletonRows /></div> : rows.length === 0 ? (
          <EmptyState icon="shield" title="Nothing held back">Unsafe files, damaged files and non-business documents land here, and are deleted after 30 days.</EmptyState>
        ) : (
          <Table rows={rows} rowKey={(r) => r.id} onRowClick={(r) => r.reason_code !== "MALWARE" && onOpen(r.id)} columns={[
            { key: "f", header: "Document", render: (r) => (
              <div className="ui-cell-main"><FileIcon item={r} /><div style={{ minWidth: 0 }}>
                <span className="ui-cell-title">{displayName(r)}</span><span className="ui-cell-sub">{sourceLabel(r.source)}</span></div></div>) },
            { key: "r", header: "Why", render: (r) => <Badge value={r.reason_code ?? "—"} tone={r.reason_code === "MALWARE" ? "red" : "grey"} /> },
            { key: "e", header: "Deleted in", render: (r) => `${daysLeft(r)} days` },
            { key: "d", header: "Received", render: (r) => <span className="ui-hint">{fmtDate(r.received_at)}</span> },
            { key: "a", header: "", render: (r) => s.can("review") && r.reason_code !== "MALWARE" ? (
              <Button size="sm" onClick={(e) => { e.stopPropagation(); act.mutate({ id: r.id, a: { action: "accept", reason: "restored" } }); }}>Restore</Button>
            ) : null },
          ]} />
        )}
        {rows.length ? (
          <div className="ui-table-foot">
            <span>{rows.length} shown · unsafe files are kept 7 days, others 30</span>
            {q.hasNextPage ? <Button size="sm" onClick={() => void q.fetchNextPage()} busy={q.isFetchingNextPage}>Load more</Button> : null}
          </div>
        ) : null}
      </Card>
    </>
  );
}
